if (!customElements.get('leasing-flow')) {
  customElements.define(
    'leasing-flow',
    class LeasingFlow extends HTMLElement {
      constructor() {
        super();
        this.monthOptions = [12, 18, 24, 36, 48];
        this.initialized = false;
        this.handleEscape = this.onEscape.bind(this);
        this.handleSourceChange = this.syncFromSource.bind(this);
      }

      connectedCallback() {
        if (this.initialized) {
          return;
        }

        this.initialized = true;
        this.scope = this.closest('[data-leasing-scope]') || this.parentElement;
        this.form = (this.scope && this.scope.closest('form')) || this.closest('form');
        this.buySurface = this.scope ? this.scope.querySelector('[data-leasing-buy-surface]') : null;

        if (!this.scope || !this.form || !this.buySurface) {
          return;
        }

        this.context = this.dataset.context || 'product-form';
        this.productTitle = this.dataset.productTitle || '';
        this.productId = this.dataset.productId || '';
        this.currencyCode = this.dataset.currencyCode || 'CHF';
        this.locale = this.dataset.locale || 'de-CH';
        this.initialPriceCents = this.toInt(this.dataset.initialPriceCents);
        this.maxDownCap = this.toInt(this.dataset.maxDown) || 3500;
        this.tokenVariantId = this.dataset.tokenVariantId || '';
        this.deliveryVariantId = this.dataset.deliveryVariantId || '';
        this.processingVariantId = this.dataset.processingVariantId || '';
        this.defaultMonths = this.toInt(this.dataset.defaultMonths) || 12;
        this.defaultDown = this.toInt(this.dataset.defaultDown);
        this.variants = this.parseJson('[data-leasing-variants]');
        const parsedTranslations = this.parseJson('[data-leasing-translations]');
        this.translations =
          parsedTranslations && typeof parsedTranslations === 'object' && !Array.isArray(parsedTranslations)
            ? parsedTranslations
            : {};

        this.moneyFormatter = new Intl.NumberFormat(this.locale, {
          style: 'currency',
          currency: this.currencyCode,
        });

        this.moneyWholeFormatter = new Intl.NumberFormat(this.locale, {
          style: 'currency',
          currency: this.currencyCode,
          minimumFractionDigits: 0,
          maximumFractionDigits: 0,
        });

        this.variantInput = this.form.querySelector('input[name="id"]');
        this.quantityInput = this.form.querySelector('input[name="quantity"]');
        this.totalCentsInput = this.form.querySelector('[data-leasing-total-cents]');
        this.configurator = this.closest('[data-stark-configurator]');

        this.modeButtons = Array.from(this.querySelectorAll('[data-leasing-mode]'));
        this.leaseSurface = this.querySelector('[data-leasing-lease-surface]');
        this.openButton = this.querySelector('[data-leasing-open]');
        this.openButtonLabel = this.querySelector('[data-leasing-open-label]');
        this.defaultOpenButtonLabel = this.openButtonLabel
          ? this.openButtonLabel.textContent.trim()
          : this.t('open_label', {}, 'Lease calculator');

        this.modal = this.querySelector('[data-leasing-modal]');
        this.closeButtons = Array.from(this.querySelectorAll('[data-leasing-close]'));
        this.stepIndicators = Array.from(this.querySelectorAll('[data-leasing-step-indicator]'));
        this.stepPanels = Array.from(this.querySelectorAll('[data-leasing-step]'));
        this.status = this.querySelector('[data-leasing-status]');

        this.monthsSelect = this.querySelector('[data-leasing-months-select]');
        this.monthsRange = this.querySelector('[data-leasing-months-range]');
        this.downInput = this.querySelector('[data-leasing-down-input]');
        this.downRange = this.querySelector('[data-leasing-down-range]');
        this.percentButtons = Array.from(this.querySelectorAll('[data-leasing-percent]'));

        this.monthlyAmount = this.querySelector('[data-leasing-monthly-amount]');
        this.monthsOutput = this.querySelector('[data-leasing-months-output]');
        this.downOutput = this.querySelector('[data-leasing-down-output]');
        this.financedOutput = this.querySelector('[data-leasing-financed-output]');
        this.teaser = this.querySelector('[data-leasing-teaser]');

        this.planContinueButton = this.querySelector('[data-leasing-next-plan]');
        this.uploadsContinueButton = this.querySelector('[data-leasing-next-uploads]');
        this.backToPlanButton = this.querySelector('[data-leasing-back-plan]');
        this.backToUploadsButton = this.querySelector('[data-leasing-back-uploads]');

        this.frontInput = this.querySelector('[data-leasing-id-front]');
        this.backInput = this.querySelector('[data-leasing-id-back]');
        this.frontPreview = this.querySelector('[data-leasing-id-front-preview]');
        this.backPreview = this.querySelector('[data-leasing-id-back-preview]');

        this.detailsForm = this.querySelector('[data-leasing-details-form]');
        this.submitButton = this.querySelector('[data-leasing-submit]');
        this.submitLabel = this.querySelector('[data-leasing-submit-label]');
        this.defaultSubmitLabel = this.submitLabel
          ? this.submitLabel.textContent.trim()
          : this.t('submit', {}, 'Add leasing to cart');
        this.currentStep = 1;
        this.isBusy = false;

        this.bindEvents();
        this.syncMonths(this.defaultMonths);
        this.downInput.value = String(this.defaultDown);
        this.syncFromSource();
        this.setMode('buy');
        this.setStep(1);
      }

      disconnectedCallback() {
        if (this.variantInput) {
          this.variantInput.removeEventListener('change', this.handleSourceChange);
        }

        if (this.totalCentsInput) {
          this.totalCentsInput.removeEventListener('change', this.handleSourceChange);
        }

        if (this.configurator) {
          this.configurator.removeEventListener('stark-configurator:updated', this.handleSourceChange);
        }

        document.removeEventListener('keydown', this.handleEscape);
      }

      bindEvents() {
        this.modeButtons.forEach((button) => {
          button.addEventListener('click', () => {
            this.setMode(button.dataset.leasingMode);
          });
        });

        if (this.openButton) {
          this.openButton.addEventListener('click', () => {
            if (!this.isBusy && !this.openButton.disabled) {
              this.openModal();
            }
          });
        }

        this.closeButtons.forEach((button) => {
          button.addEventListener('click', () => this.closeModal());
        });

        if (this.monthsSelect) {
          this.monthsSelect.addEventListener('change', () => {
            this.syncMonths(this.toInt(this.monthsSelect.value));
            this.recalculate();
          });
        }

        if (this.monthsRange) {
          this.monthsRange.addEventListener('input', () => {
            const index = this.toInt(this.monthsRange.value);
            const months = this.monthOptions[Math.max(0, Math.min(index, this.monthOptions.length - 1))] || this.monthOptions[1];
            this.syncMonths(months);
            this.recalculate();
          });
        }

        if (this.downInput) {
          ['input', 'change'].forEach((eventName) => {
            this.downInput.addEventListener(eventName, () => {
              this.syncDownValue(this.toInt(this.downInput.value));
              this.recalculate();
            });
          });
        }

        if (this.downRange) {
          ['input', 'change'].forEach((eventName) => {
            this.downRange.addEventListener(eventName, () => {
              this.syncDownValue(this.toInt(this.downRange.value));
              this.recalculate();
            });
          });
        }

        this.percentButtons.forEach((button) => {
          button.addEventListener('click', () => {
            const percent = this.toInt(button.dataset.leasingPercent);
            const price = this.getCurrentPriceValue();
            this.syncDownValue(Math.round((price * percent) / 100));
            this.recalculate();
          });
        });

        if (this.planContinueButton) {
          this.planContinueButton.addEventListener('click', () => {
            this.clearStatus();
            this.setStep(2);
          });
        }

        if (this.uploadsContinueButton) {
          this.uploadsContinueButton.addEventListener('click', () => {
            if (!this.getFile(this.frontInput) || !this.getFile(this.backInput)) {
              this.setStatus(this.t('error_upload_continue', {}, 'Upload both sides of the ID before continuing.'), 'error');
              return;
            }

            this.clearStatus();
            this.setStep(3);
          });
        }

        if (this.backToPlanButton) {
          this.backToPlanButton.addEventListener('click', () => {
            this.clearStatus();
            this.setStep(1);
          });
        }

        if (this.backToUploadsButton) {
          this.backToUploadsButton.addEventListener('click', () => {
            this.clearStatus();
            this.setStep(2);
          });
        }

        if (this.frontInput) {
          this.frontInput.addEventListener('change', () => {
            this.updatePreview(this.frontPreview, this.getFile(this.frontInput));
          });
        }

        if (this.backInput) {
          this.backInput.addEventListener('change', () => {
            this.updatePreview(this.backPreview, this.getFile(this.backInput));
          });
        }

        if (this.submitButton) {
          this.submitButton.addEventListener('click', (event) => {
            event.preventDefault();

            this.handleDetailsSubmit();
          });
        }

        if (this.detailsForm) {
          this.detailsForm.addEventListener('keydown', (event) => {
            const target = event.target;
            const tagName = target && target.tagName ? target.tagName.toLowerCase() : '';

            if (event.key !== 'Enter' || tagName === 'textarea' || tagName === 'select' || tagName === 'button') {
              return;
            }

            event.preventDefault();
            this.handleDetailsSubmit();
          });
        }

        if (this.variantInput) {
          this.variantInput.addEventListener('change', this.handleSourceChange);
        }

        if (this.totalCentsInput) {
          this.totalCentsInput.addEventListener('change', this.handleSourceChange);
        }

        if (this.configurator) {
          this.configurator.addEventListener('stark-configurator:updated', this.handleSourceChange);
        }

        document.addEventListener('keydown', this.handleEscape);
      }

      onEscape(event) {
        if (event.key === 'Escape' && this.modal && !this.modal.hidden) {
          this.closeModal();
        }
      }

      handleDetailsSubmit() {
        if (this.isBusy) {
          return;
        }

        if (!this.validateDetailsForm()) {
          return;
        }

        this.submitLease();
      }

      setMode(mode) {
        const isLease = mode === 'lease';

        this.modeButtons.forEach((button) => {
          const isActive = button.dataset.leasingMode === mode;
          button.classList.toggle('is-active', isActive);
          button.setAttribute('aria-pressed', isActive ? 'true' : 'false');
        });

        this.buySurface.hidden = isLease;
        if (this.leaseSurface) {
          this.leaseSurface.hidden = !isLease;
        }

        if (!isLease) {
          this.closeModal();
        }
      }

      openModal() {
        if (!this.modal) {
          return;
        }

        this.modal.hidden = false;
        this.modal.classList.add('is-open');
        document.body.style.overflow = 'hidden';
        this.setStep(1);
        this.clearStatus();
      }

      closeModal() {
        if (!this.modal) {
          return;
        }

        this.modal.hidden = true;
        this.modal.classList.remove('is-open');
        document.body.style.overflow = '';
      }

      setStep(step) {
        this.currentStep = step;
        this.stepIndicators.forEach((indicator) => {
          indicator.classList.toggle('is-active', this.toInt(indicator.dataset.leasingStepIndicator) === step);
        });

        this.stepPanels.forEach((panel) => {
          panel.hidden = this.toInt(panel.dataset.leasingStep) !== step;
        });
      }

      syncMonths(months) {
        const safeMonths = this.monthOptions.includes(months) ? months : this.monthOptions[1];
        const index = this.monthOptions.indexOf(safeMonths);

        if (this.monthsSelect) {
          this.monthsSelect.value = String(safeMonths);
        }

        if (this.monthsRange) {
          this.monthsRange.value = String(index);
        }
      }

      syncDownBounds() {
        const maxDown = this.getMaxDownValue();
        const safeValue = Math.min(Math.max(this.toInt(this.downInput ? this.downInput.value : 0), 0), maxDown);

        if (this.downInput) {
          this.downInput.max = String(maxDown);
          this.downInput.value = String(safeValue);
        }

        if (this.downRange) {
          this.downRange.max = String(maxDown);
          this.downRange.value = String(safeValue);
        }
      }

      syncDownValue(value) {
        const maxDown = this.getMaxDownValue();
        const safeValue = Math.min(Math.max(this.toInt(value), 0), maxDown);

        if (this.downInput) {
          this.downInput.value = String(safeValue);
        }

        if (this.downRange) {
          this.downRange.value = String(safeValue);
        }
      }

      getSelectedMonths() {
        const months = this.toInt(this.monthsSelect ? this.monthsSelect.value : this.defaultMonths);
        return this.monthOptions.includes(months) ? months : this.defaultMonths;
      }

      getSelectedVariant() {
        const variantId = this.variantInput ? String(this.variantInput.value || '') : '';

        if (!variantId) {
          return null;
        }

        return this.variants.find((variant) => String(variant.id) === variantId) || null;
      }

      getCurrentPriceCents() {
        if (this.context === 'stark-configurator' && this.totalCentsInput) {
          const total = this.toInt(this.totalCentsInput.value);
          if (total > 0) {
            return total;
          }
        }

        const variant = this.getSelectedVariant();
        if (variant && typeof variant.price === 'number') {
          return variant.price;
        }

        return this.initialPriceCents;
      }

      getCurrentPriceValue() {
        return this.getCurrentPriceCents() / 100;
      }

      getCurrentPreviewImageUrl(variant) {
        if (this.context === 'stark-configurator' && this.configurator) {
          const selectedColor = this.configurator.querySelector('[data-config-option][data-step="color"].is-selected');
          const colorSrc = selectedColor ? selectedColor.dataset.image || selectedColor.dataset.stageImage || '' : '';

          if (colorSrc) {
            return colorSrc;
          }
        }

        if (variant) {
          if (variant.featured_image && variant.featured_image.src) {
            return String(variant.featured_image.src);
          }

          if (variant.image && variant.image.src) {
            return String(variant.image.src);
          }

          if (variant.featured_media && variant.featured_media.preview_image && variant.featured_media.preview_image.src) {
            return String(variant.featured_media.preview_image.src);
          }
        }

        return '';
      }

      getMaxDownValue() {
        return Math.max(0, Math.min(this.maxDownCap, Math.round(this.getCurrentPriceValue())));
      }

      getPlan() {
        const months = this.getSelectedMonths();
        const price = this.getCurrentPriceValue();
        const down = Math.min(this.getMaxDownValue(), Math.max(0, this.toInt(this.downInput ? this.downInput.value : 0)));
        const financed = Math.max(0, price - down);
        const monthly = financed / Math.max(months, 1);

        return {
          months: months,
          price: price,
          down: down,
          financed: financed,
          monthly: monthly,
          financedRounded: Math.round(financed),
          monthlyRounded: Math.round(monthly),
        };
      }

      recalculate() {
        const plan = this.getPlan();
        const priceAvailable = plan.price > 0;

        if (this.monthlyAmount) {
          this.monthlyAmount.textContent = priceAvailable
            ? this.formatMoney(plan.monthly, true)
            : this.t('price_pending', {}, 'Price pending');
        }

        if (this.monthsOutput) {
          this.monthsOutput.textContent = String(plan.months);
        }

        if (this.downOutput) {
          this.downOutput.textContent = this.formatMoney(plan.down, true);
        }

        if (this.financedOutput) {
          this.financedOutput.textContent = priceAvailable
            ? this.formatMoney(plan.financed)
            : this.t('price_pending', {}, 'Price pending');
        }

        if (this.teaser) {
          if (!priceAvailable) {
            this.teaser.textContent = this.t(
              'teaser_price_pending',
              {},
              'Leasing becomes available once a price has been set for this product.'
            );
          } else if (plan.financedRounded > 0) {
            this.teaser.textContent = this.t(
              'teaser_estimate',
              {
                amount: this.formatMoney(plan.monthly, true),
                months: String(plan.months),
              },
              'Estimated from {{ amount }} / month over {{ months }} months.'
            );
          } else {
            this.teaser.textContent = this.t(
              'teaser_down_payment',
              {},
              'Choose a lower down payment to create a leasing plan.'
            );
          }
        }

        const price = plan.price;
        const percent = price > 0 ? Math.round((plan.down / price) * 100) : 0;
        this.percentButtons.forEach((button) => {
          button.classList.toggle('is-active', this.toInt(button.dataset.leasingPercent) === percent);
        });
      }

      syncFromSource() {
        this.syncDownBounds();
        this.recalculate();

        const variant = this.getSelectedVariant();
        const priceAvailable = this.getCurrentPriceValue() > 0;
        const isAvailable = this.context === 'stark-configurator'
          ? !variant || variant.available !== false
          : !!variant && variant.available !== false;

        if (this.openButton) {
          this.openButton.disabled = !isAvailable || !priceAvailable || this.isBusy;
        }

        if (this.openButtonLabel) {
          this.openButtonLabel.textContent = this.defaultOpenButtonLabel;
        }
      }

      getFile(input) {
        return input && input.files && input.files[0] ? input.files[0] : null;
      }

      updatePreview(container, file) {
        if (!container) {
          return;
        }

        const image = container.querySelector('img');
        const placeholder = container.querySelector('[data-leasing-placeholder]');

        if (!file || !image) {
          if (image) {
            image.removeAttribute('src');
            image.hidden = true;
          }
          if (placeholder) {
            placeholder.hidden = false;
          }
          return;
        }

        const objectUrl = URL.createObjectURL(file);
        image.src = objectUrl;
        image.hidden = false;
        if (placeholder) {
          placeholder.hidden = true;
        }

        image.onload = () => {
          URL.revokeObjectURL(objectUrl);
        };
      }

      getDetailsFields() {
        if (!this.detailsForm) {
          return [];
        }

        return Array.from(this.detailsForm.querySelectorAll('input[name], select[name], textarea[name]'));
      }

      validateDetailsForm() {
        if (!this.detailsForm) {
          return true;
        }

        if (typeof this.detailsForm.reportValidity === 'function') {
          return this.detailsForm.reportValidity();
        }

        for (const field of this.getDetailsFields()) {
          if (typeof field.reportValidity === 'function' && !field.reportValidity()) {
            return false;
          }
        }

        return true;
      }

      getDetailValue(name) {
        if (!this.detailsForm) {
          return '';
        }

        const field =
          this.detailsForm.querySelector(`[name="${name}"]:checked`) ||
          this.detailsForm.querySelector(`[name="${name}"]`);
        return field ? String(field.value || '').trim() : '';
      }

      collectCustomerDetails() {
        const birthDay = this.getDetailValue('birth_day').padStart(2, '0');
        const birthMonth = this.getDetailValue('birth_month').padStart(2, '0');
        const birthYear = this.getDetailValue('birth_year');

        return {
          salutation: this.getDetailValue('salutation'),
          firstName: this.getDetailValue('first_name'),
          lastName: this.getDetailValue('last_name'),
          street: this.getDetailValue('street'),
          postalCode: this.getDetailValue('postal_code'),
          city: this.getDetailValue('city'),
          dateOfBirth: `${birthDay}.${birthMonth}.${birthYear}`,
          nationality: this.getDetailValue('nationality'),
          mobile: this.getDetailValue('mobile'),
          email: this.getDetailValue('email'),
          language: this.getDetailValue('language'),
        };
      }

      buildExistingProperties() {
        const properties = {};

        Array.from(
          this.form.querySelectorAll('input[name^="properties["], textarea[name^="properties["], select[name^="properties["]')
        ).forEach((input) => {
          const match = input.name.match(/^properties\[(.*)\]$/);
          const key = match && match[1] ? match[1] : null;

          if (!key || key.charAt(0) === '_' || input.value === '') {
            return;
          }

          properties[key] = input.value;
        });

        return properties;
      }

      buildLeasingProperties(plan, variant, details) {
        return Object.assign({}, this.buildExistingProperties(), {
          '_Leasing Marker': this.tokenVariantId ? 'token' : 'product',
          '_Leasing Product URL': window.location.pathname || '',
          '_Leasing Preview Image': this.getCurrentPreviewImageUrl(variant),
          'Leasing: Product': this.productTitle,
          'Leasing: Variant': variant && variant.title ? String(variant.title) : '',
          '_Leasing Product ID': this.productId,
          '_Leasing Variant ID': variant && variant.id ? String(variant.id) : '',
          'Leasing: Term (months)': String(plan.months),
          'Leasing: Down payment (CHF)': this.formatMoney(plan.down),
          'Leasing: Product price (CHF)': this.formatMoney(plan.price),
          'Leasing: Financed (CHF)': this.formatMoney(plan.financed),
          'Leasing: Monthly (CHF)': this.formatMoney(plan.monthly, true),
          'Leasing: Salutation': details.salutation,
          'Leasing: First name': details.firstName,
          'Leasing: Last name': details.lastName,
          'Leasing: Street / no.': details.street,
          'Leasing: Postal code': details.postalCode,
          'Leasing: City': details.city,
          'Leasing: Date of birth': details.dateOfBirth,
          'Leasing: Nationality': details.nationality,
          'Leasing: Mobile': details.mobile,
          'Leasing: Email': details.email,
          'Leasing: Language': details.language,
        });
      }

      async submitLease() {
        const variant = this.getSelectedVariant();

        if (this.context !== 'stark-configurator' && (!variant || variant.available === false)) {
          this.setStatus(this.t('error_select_variant', {}, 'Select an available variant before continuing with leasing.'), 'error');
          return;
        }

        const frontFile = this.getFile(this.frontInput);
        const backFile = this.getFile(this.backInput);

        if (!frontFile || !backFile) {
          this.setStatus(
            this.t('error_upload_submit', {}, 'Upload both sides of the ID before submitting the leasing request.'),
            'error'
          );
          this.setStep(2);
          return;
        }

        const plan = this.getPlan();
        if (plan.financedRounded < 1) {
          this.setStatus(
            this.t(
              'error_financed_amount',
              {},
              'The financed amount must stay above CHF 0. Reduce the down payment or use the regular buy flow.'
            ),
            'error'
          );
          this.setStep(1);
          return;
        }

        if (!this.tokenVariantId) {
          this.setStatus(this.t('error_token_missing', {}, 'The leasing token variant is not configured on this product yet.'), 'error');
          return;
        }

        const details = this.collectCustomerDetails();

        this.setBusy(true, this.t('busy_adding_to_cart', {}, 'Adding leasing to cart...'));

        try {
          const pdfFile = await this.buildPdfFile(plan, variant, details);
          const properties = this.buildLeasingProperties(plan, variant, details);
          const filePayload = {
            '_Leasing ID front': frontFile,
            '_Leasing ID back': backFile,
            '_Leasing Application PDF': pdfFile,
          };
          const cleanupVariantIds = [this.tokenVariantId, this.deliveryVariantId, this.processingVariantId].filter(Boolean);

          await this.cleanupCartBeforeAdd(cleanupVariantIds);

          await this.addSingleItemViaFormData({
            variantId: this.tokenVariantId,
            quantity: plan.financedRounded,
            properties: properties,
            files: filePayload,
          });

          const feeItems = this.buildFeeItems();
          if (feeItems.length) {
            await this.addItems(feeItems);
          }

          window.location.href = '/cart';
        } catch (error) {
          this.setBusy(false);
          this.setStatus(
            error && error.message ? error.message : this.t('error_create_request', {}, 'Unable to create the leasing request. Please try again.'),
            'error'
          );
        }
      }

      buildFeeItems() {
        const items = [];

        if (this.deliveryVariantId) {
          items.push({
            id: Number(this.deliveryVariantId),
            quantity: 1,
            properties: {
              '_Leasing Marker': 'fee',
              'Leasing: Product': this.productTitle,
              'Leasing: Fee': 'Delivery',
            },
          });
        }

        if (this.processingVariantId) {
          items.push({
            id: Number(this.processingVariantId),
            quantity: 1,
            properties: {
              '_Leasing Marker': 'fee',
              'Leasing: Product': this.productTitle,
              'Leasing: Fee': 'Processing',
            },
          });
        }

        return items.filter((item) => Number.isFinite(item.id) && item.id > 0);
      }

      async cleanupCartBeforeAdd(variantIds) {
        const response = await fetch('/cart.js', {
          credentials: 'same-origin',
        });

        if (!response.ok) {
          return;
        }

        const cart = await response.json();
        const updates = {};
        const variantIdSet = new Set((variantIds || []).map((value) => String(value)));

        (cart.items || []).forEach((item) => {
          const marker = item && item.properties ? item.properties['_Leasing Marker'] || item.properties['Leasing: Marker'] : '';
          if (marker || variantIdSet.has(String(item.variant_id))) {
            updates[item.key] = 0;
          }
        });

        if (!Object.keys(updates).length) {
          return;
        }

        const updateResponse = await fetch('/cart/update.js', {
          method: 'POST',
          credentials: 'same-origin',
          headers: {
            'Content-Type': 'application/json',
            Accept: 'application/json',
            'X-Requested-With': 'XMLHttpRequest',
          },
          body: JSON.stringify({ updates: updates }),
        });

        if (!updateResponse.ok) {
          throw new Error(this.t('error_clear_existing', {}, 'Unable to clear the existing leasing request from the cart.'));
        }
      }

      async addItems(items) {
        const response = await fetch('/cart/add.js', {
          method: 'POST',
          credentials: 'same-origin',
          headers: {
            'Content-Type': 'application/json',
            Accept: 'application/json',
            'X-Requested-With': 'XMLHttpRequest',
          },
          body: JSON.stringify({ items: items }),
        });

        const payload = await response.json().catch(() => null);
        if (!response.ok || (payload && payload.status)) {
          throw new Error(
            (payload && (payload.description || payload.message)) ||
              this.t('error_add_fee_items', {}, 'Unable to add the leasing fee items to the cart.')
          );
        }

        return payload;
      }

      async addSingleItemViaFormData(payload) {
        const formData = new FormData();
        formData.append('form_type', 'product');
        formData.append('id', String(payload.variantId));
        formData.append('quantity', String(payload.quantity));

        Object.keys(payload.properties || {}).forEach((key) => {
          const value = payload.properties[key];
          if (value !== undefined && value !== null && value !== '') {
            formData.append('properties[' + key + ']', String(value));
          }
        });

        Object.keys(payload.files || {}).forEach((key) => {
          const file = payload.files[key];
          if (file instanceof File) {
            formData.append('properties[' + key + ']', file, file.name || 'upload');
          }
        });

        return new Promise((resolve, reject) => {
          const request = new XMLHttpRequest();
          request.open('POST', '/cart/add.js');
          request.responseType = 'json';
          request.setRequestHeader('Accept', 'application/json');
          request.setRequestHeader('X-Requested-With', 'XMLHttpRequest');

          request.onload = () => {
            const payloadResponse = request.response || null;
            if (request.status >= 200 && request.status < 300 && !(payloadResponse && payloadResponse.status)) {
              resolve(payloadResponse);
              return;
            }

            reject(
              new Error(
                (payloadResponse && (payloadResponse.description || payloadResponse.message)) ||
                  this.t('error_add_request', {}, 'Unable to add the leasing request to the cart.')
              )
            );
          };

          request.onerror = () => {
            reject(new Error(this.t('error_network_add_request', {}, 'Network error while adding the leasing request to the cart.')));
          };

          request.send(formData);
        });
      }

      async buildPdfFile(plan, variant, details) {
        const { jsPDF } = await this.ensureJsPdfLoaded();
        const doc = new jsPDF({ unit: 'pt', format: 'a4' });
        const margin = 48;
        const maxWidth = doc.internal.pageSize.getWidth() - margin * 2;
        let cursorY = margin;

        const writeLine = (text, options) => {
          const settings = Object.assign(
            {
              size: 10.5,
              style: 'normal',
              color: [17, 17, 17],
              gap: 14,
            },
            options || {}
          );

          doc.setFont('helvetica', settings.style);
          doc.setFontSize(settings.size);
          doc.setTextColor.apply(doc, settings.color);
          const lines = doc.splitTextToSize(String(text || ''), maxWidth);
          const nextHeight = lines.length * settings.gap;

          if (cursorY + nextHeight > doc.internal.pageSize.getHeight() - margin) {
            doc.addPage();
            cursorY = margin;
          }

          doc.text(lines, margin, cursorY);
          cursorY += nextHeight;
        };

        const configurationEntries = Object.entries(this.buildExistingProperties());

        writeLine(this.t('pdf_title', {}, 'Leasing application'), { size: 18, style: 'bold', gap: 22 });
        writeLine(this.t('pdf_created', {}, 'Created') + ': ' + new Date().toISOString(), { color: [75, 85, 99] });
        writeLine(this.t('pdf_product', {}, 'Product') + ': ' + this.productTitle, { style: 'bold', gap: 18 });
        if (variant && variant.title) {
          writeLine(this.t('pdf_variant', {}, 'Variant') + ': ' + variant.title);
        }

        writeLine(this.t('pdf_plan_details', {}, 'Plan details'), { size: 13, style: 'bold', gap: 18 });
        writeLine(this.t('pdf_product_price', {}, 'Product price') + ': ' + this.formatMoney(plan.price));
        writeLine(this.t('pdf_down_payment', {}, 'Down payment') + ': ' + this.formatMoney(plan.down));
        writeLine(this.t('pdf_financed_amount', {}, 'Financed amount') + ': ' + this.formatMoney(plan.financed));
        writeLine(this.t('pdf_monthly_payment', {}, 'Monthly payment') + ': ' + this.formatMoney(plan.monthly, true));
        writeLine(
          this.t('pdf_term', {}, 'Term') +
            ': ' +
            this.t('pdf_term_value', { months: String(plan.months) }, String(plan.months) + ' months')
        );

        if (configurationEntries.length) {
          writeLine(this.t('pdf_configuration', {}, 'Configuration'), { size: 13, style: 'bold', gap: 18 });
          configurationEntries.forEach((entry) => {
            writeLine(entry[0] + ': ' + entry[1]);
          });
        }

        writeLine(this.t('pdf_customer_details', {}, 'Customer details'), { size: 13, style: 'bold', gap: 18 });
        writeLine(this.t('pdf_salutation', {}, 'Salutation') + ': ' + details.salutation);
        writeLine(this.t('pdf_first_name', {}, 'First name') + ': ' + details.firstName);
        writeLine(this.t('pdf_last_name', {}, 'Last name') + ': ' + details.lastName);
        writeLine(this.t('pdf_street', {}, 'Street / no.') + ': ' + details.street);
        writeLine(this.t('pdf_postal_code', {}, 'Postal code') + ': ' + details.postalCode);
        writeLine(this.t('pdf_city', {}, 'City') + ': ' + details.city);
        writeLine(this.t('pdf_date_of_birth', {}, 'Date of birth') + ': ' + details.dateOfBirth);
        writeLine(this.t('pdf_nationality', {}, 'Nationality') + ': ' + details.nationality);
        writeLine(this.t('pdf_mobile', {}, 'Mobile') + ': ' + details.mobile);
        writeLine(this.t('pdf_email', {}, 'Email') + ': ' + details.email);
        writeLine(this.t('pdf_language', {}, 'Language') + ': ' + details.language);

        const blob = doc.output('blob');
        const safeTitle = (this.productTitle || 'product')
          .toLowerCase()
          .replace(/[^a-z0-9]+/g, '-')
          .replace(/^-+|-+$/g, '')
          .slice(0, 48);
        return new File([blob], 'leasing-' + (safeTitle || 'application') + '.pdf', {
          type: 'application/pdf',
        });
      }

      async ensureJsPdfLoaded() {
        if (window.jspdf && window.jspdf.jsPDF) {
          return window.jspdf;
        }

        await this.loadScriptOnce('https://cdnjs.cloudflare.com/ajax/libs/jspdf/2.5.1/jspdf.umd.min.js');

        if (!(window.jspdf && window.jspdf.jsPDF)) {
          throw new Error(this.t('error_pdf_library', {}, 'Unable to load the PDF library for the leasing application.'));
        }

        return window.jspdf;
      }

      async loadScriptOnce(url) {
        const existing = document.querySelector('script[src="' + url + '"]');
        if (existing) {
          if (existing.dataset.loaded === 'true') {
            return;
          }

          await new Promise((resolve, reject) => {
            existing.addEventListener('load', resolve, { once: true });
            existing.addEventListener('error', () => reject(new Error(this.t('error_script_load', {}, 'Script load failed.'))), { once: true });
          });
          return;
        }

        await new Promise((resolve, reject) => {
          const script = document.createElement('script');
          script.src = url;
          script.async = true;
          script.addEventListener(
            'load',
            () => {
              script.dataset.loaded = 'true';
              resolve();
            },
            { once: true }
          );
          script.addEventListener('error', () => reject(new Error(this.t('error_script_load', {}, 'Script load failed.'))), { once: true });
          document.head.appendChild(script);
        });
      }

      setBusy(isBusy, message) {
        this.isBusy = isBusy;

        if (this.submitButton) {
          this.submitButton.disabled = isBusy;
          this.submitButton.classList.toggle('is-busy', isBusy);
          this.submitButton.setAttribute('aria-busy', String(isBusy));
          const submitActions = this.submitButton.closest('[data-leasing-step-actions], .leasing-flow__actions');
          if (submitActions) {
            submitActions.classList.toggle('is-busy', isBusy);
          }
        }

        if (this.submitLabel) {
          this.submitLabel.textContent = isBusy ? this.t('submit_busy', {}, 'Preparing lease...') : this.defaultSubmitLabel;
        }

        if (this.planContinueButton) {
          this.planContinueButton.disabled = isBusy;
        }

        if (this.uploadsContinueButton) {
          this.uploadsContinueButton.disabled = isBusy;
        }

        if (message) {
          this.setStatus(message, 'info');
        } else if (!isBusy) {
          this.clearStatus();
        }

        this.syncFromSource();
      }

      setStatus(message, state) {
        if (!this.status) {
          return;
        }

        if (!message) {
          this.clearStatus();
          return;
        }

        this.status.hidden = false;
        this.status.dataset.state = state || 'info';
        this.status.textContent = message;
      }

      clearStatus() {
        if (!this.status) {
          return;
        }

        this.status.hidden = true;
        this.status.textContent = '';
        delete this.status.dataset.state;
      }

      t(key, replacements, fallback) {
        const template = this.translations && typeof this.translations[key] === 'string' ? this.translations[key] : fallback || '';
        return this.interpolate(template, replacements);
      }

      interpolate(template, replacements) {
        const values = replacements || {};
        return String(template || '').replace(/\{\{\s*(\w+)\s*\}\}/g, (match, token) => {
          return Object.prototype.hasOwnProperty.call(values, token) ? String(values[token]) : '';
        });
      }

      parseJson(selector) {
        const node = this.querySelector(selector);

        if (!node) {
          return [];
        }

        try {
          return JSON.parse(node.textContent);
        } catch (error) {
          return [];
        }
      }

      formatMoney(value, whole) {
        const amount = Number(value) || 0;
        return (whole ? this.moneyWholeFormatter : this.moneyFormatter).format(amount);
      }

      toInt(value) {
        const number = parseInt(value, 10);
        return Number.isFinite(number) ? number : 0;
      }
    }
  );
}